import { UiSession } from '@brighthub/uisession';

// ---------------------------------------------------------------------------
// Core union types
// ---------------------------------------------------------------------------

export type QueueBucket = 'now' | 'next' | 'later';

export type PresenceState = 'active' | 'recent' | 'offline';

/** Operational AI/FA handoff state — null means no special handoff active. */
export type HandoffState = 'ai_in_progress' | 'needs_fa' | 'escalated' | null;

export type UrgencyBadge = 'overdue' | 'due_soon' | 'on_track' | 'waiting_on_client';

export type InteractionChannel = 'email' | 'sms' | 'call' | 'chat';

export type QueueFilter = 'all' | 'needs_fa' | 'ai_handling' | 'waiting_10m' | 'upcoming_call';

// ---------------------------------------------------------------------------
// Rank reason — one explainable factor contributing to a row's score
// ---------------------------------------------------------------------------

export interface RankReason {
  /** Human-readable label shown in the tooltip. */
  label: string;
  /** The points this factor contributed to the score. */
  weight: number;
}

// ---------------------------------------------------------------------------
// Enriched row — a UiSession augmented with operational metadata
// ---------------------------------------------------------------------------

export interface EnrichedQueueRow {
  /** The underlying real session from the API. */
  session: UiSession;

  // --- Presence ---
  presence: PresenceState;
  /** Minutes since the client was last active (0 = active now). */
  lastActiveMinutesAgo: number;

  // --- Last interaction ---
  lastChannel: InteractionChannel;
  /** Minutes since the last interaction on that channel. */
  lastChannelMinutesAgo: number;

  // --- Wait / SLA ---
  /** How long the client has been waiting for a response (minutes). */
  waitingMinutes: number;
  urgency: UrgencyBadge;

  // --- AI/FA handoff ---
  handoff: HandoffState;

  // --- Scheduling ---
  hasUpcomingCall: boolean;
  /** ISO timestamp of the upcoming call, or null. */
  upcomingCallAt: string | null;

  // --- Computed by priority engine ---
  priorityScore: number;
  bucket: QueueBucket;
  /** Top 2 contributing factors for the rank-reason tooltip. */
  rankReasons: RankReason[];

  /**
   * Stable sort index assigned on first load.
   * Rows within a bucket are ordered by this index, not live score,
   * to prevent visual jitter on timer ticks.
   */
  stableIndex: number;
}

// ---------------------------------------------------------------------------
// Queue section — one of Now / Next / Later
// ---------------------------------------------------------------------------

export interface QueueSection {
  bucket: QueueBucket;
  /** Display label: 'Now', 'Next', 'Later'. */
  label: string;
  rows: EnrichedQueueRow[];
}

// ---------------------------------------------------------------------------
// Quick action identifiers
// ---------------------------------------------------------------------------

export type QueueRowAction = 'open_chat' | 'assign_to_me' | 'snooze' | 'archive' | 'call_client' | 'email_client';

export interface QueueRowActionEvent {
  action: QueueRowAction;
  row: EnrichedQueueRow;
}

// ---------------------------------------------------------------------------
// Filter chip display metadata
// ---------------------------------------------------------------------------

export interface QueueFilterChip {
  id: QueueFilter;
  label: string;
}

export const QUEUE_FILTER_CHIPS: QueueFilterChip[] = [
  { id: 'all',           label: 'All' },
  { id: 'needs_fa',      label: 'Needs FA' },
  { id: 'ai_handling',   label: 'AI Handling' },
  { id: 'waiting_10m',   label: 'Waiting > 10m' },
  { id: 'upcoming_call', label: 'Upcoming call' },
];
